import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import type { PasskeyInfo, AuthResponse, LoginTarget } from '@aegislab/shared';
import { authApi } from '../api/authApi';
import { useAuthStore } from '@/shared/model';

const passkeysQueryKey = (userId: string | null) => ['passkeys', userId] as const;

export const usePasskeys = () => {
  const userId = useAuthStore((s) => s.user?.id ?? null);

  return useQuery<PasskeyInfo[]>({
    queryKey: passkeysQueryKey(userId),
    queryFn: async () => (await authApi.listPasskeys()) as PasskeyInfo[],
    enabled: !!userId,
  });
};

function getWebAuthnErrorMessage(err: unknown): string | null {
  if (!(err instanceof Error)) return null;

  // @simplewebauthn/browser v13 wraps DOMException in WebAuthnError (extends Error)
  // with a `code` property and original error in `cause`
  const code = (err as { code?: string }).code;

  if (code === 'ERROR_INVALID_DOMAIN' || code === 'ERROR_INVALID_RP_ID') {
    return '도메인 설정 오류입니다. 관리자에게 문의해주세요.';
  }
  if (code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED') {
    return '이미 등록된 패스키입니다.';
  }
  if (code === 'ERROR_AUTHENTICATOR_MISSING_DISCOVERABLE_CREDENTIAL_SUPPORT') {
    return '이 기기는 패스키를 지원하지 않습니다. "다른 기기로 등록"을 이용해주세요.';
  }
  if (code === 'ERROR_AUTHENTICATOR_MISSING_USER_VERIFICATION_SUPPORT') {
    return '기기에서 사용자 인증을 수행할 수 없습니다. PIN 또는 생체인증을 설정해주세요.';
  }

  // Check error name (WebAuthnError preserves the original DOMException name)
  switch (err.name) {
    case 'NotAllowedError':
      return '인증이 거부되었거나 시간이 초과되었습니다. 기기에 PIN/생체인증이 설정되어 있는지 확인하거나, "다른 기기로 등록"을 이용해주세요.';
    case 'SecurityError':
      return '보안 오류가 발생했습니다. HTTPS 연결을 확인해주세요.';
    case 'NotSupportedError':
      return '이 기기에서 지원하지 않는 인증 방식입니다. "다른 기기로 등록"을 이용해주세요.';
    case 'InvalidStateError':
      return '이미 등록된 패스키입니다.';
    case 'AbortError':
      return '패스키 등록이 취소되었습니다.';
  }

  // For ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY, check original cause
  if (code === 'ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY' && err.cause instanceof Error) {
    return getWebAuthnErrorMessage(err.cause);
  }

  return null;
}

export const useRegisterPasskey = () => {
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id ?? null);

  return useMutation({
    mutationFn: async ({
      friendlyName,
      crossPlatform,
    }: { friendlyName?: string; crossPlatform?: boolean } = {}) => {
      // Step 1: Get registration options from server
      const optionsJSON = (await authApi.getPasskeyRegistrationOptions(
        crossPlatform
      )) as Parameters<typeof startRegistration>[0]['optionsJSON'];

      // Step 2: Trigger browser WebAuthn prompt
      let attResp;
      try {
        attResp = await startRegistration({ optionsJSON });
      } catch (err) {
        const message = getWebAuthnErrorMessage(err);
        if (message) throw new Error(message, { cause: err });
        throw err;
      }

      // Step 3: Send attestation to server for verification
      const result = await authApi.verifyPasskeyRegistration(
        attResp as unknown as Record<string, unknown>,
        friendlyName
      );

      return result;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: passkeysQueryKey(userId) });
    },
    onError: (err: unknown) => {
      // 사용자가 WebAuthn 등록 프롬프트를 취소(NotAllowedError)한 경우는 정상 흐름이므로
      // 글로벌 mutation 토스트 핸들러가 무시할 수 있도록 명시적으로 처리한다.
      //
      // 이 훅은 usePasskeyLogin 과 달리 onError 를 유지한다 — 유일한 소비처인
      // PasskeyManageModal 이 취소가 아닌 실패를 이미 인라인 에러로 띄우므로(:128)
      // 전역 토스트까지 열면 같은 실패가 두 번 뜬다. 로그인 쪽은 소비처의 catch 가
      // 비어 있어 전역 토스트가 유일한 표면이라 사정이 반대다.
      if (err instanceof Error) {
        const isCancel =
          err.name === 'NotAllowedError' ||
          err.message.includes('인증이 거부되었거나 시간이 초과') ||
          err.message.includes('패스키 등록이 취소');
        if (isCancel) return;
      }
    },
  });
};

export const useDeletePasskey = () => {
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id ?? null);

  return useMutation({
    mutationFn: (passkeyId: string) => authApi.deletePasskey(passkeyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: passkeysQueryKey(userId) });
    },
  });
};

export const useRenamePasskey = () => {
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id ?? null);

  const mutation = useMutation({
    mutationFn: ({ passkeyId, friendlyName }: { passkeyId: string; friendlyName: string }) =>
      authApi.renamePasskey(passkeyId, friendlyName),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: passkeysQueryKey(userId) });
    },
  });

  const rename = useCallback(
    (passkeyId: string, friendlyName: string) => mutation.mutateAsync({ passkeyId, friendlyName }),
    [mutation]
  );

  return { ...mutation, rename };
};

export const usePasskeyLogin = () => {
  const { setAuth } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (target: LoginTarget) => {
      // Step 1: Get authentication options
      const optionsJSON = (await authApi.getPasskeyLoginOptions(target)) as Parameters<
        typeof startAuthentication
      >[0]['optionsJSON'];

      // Step 2: Trigger browser WebAuthn prompt
      let asseResp;
      try {
        asseResp = await startAuthentication({ optionsJSON });
      } catch (err) {
        if (err instanceof Error && err.name === 'NotAllowedError') {
          throw new Error(
            '이 브라우저 프로필에서는 패스키가 등록되어 있지 않습니다. 등록하신 브라우저 프로필에서 진행해주세요.',
            { cause: err }
          );
        }
        throw err;
      }

      // Step 3: Verify with server and get tokens
      const result = (await authApi.verifyPasskeyLogin(
        target,
        asseResp as unknown as Record<string, unknown>
      )) as AuthResponse;

      return result;
    },
    onSuccess: (response: AuthResponse) => {
      // useAuth 의 비밀번호/OTP 뮤테이션과 동일 순서(clear → setAuth).
      queryClient.clear();
      setAuth(response.user);
    },
    // onError 를 두지 않는다 — 이유는 useRegisterPasskey 주석 참조. 여기에 onError 가
    // 있던 동안 429 를 포함한 모든 로그인 실패가 무음이었다.
  });
};
